import React, { useState } from "react";
import { useTranslation } from "react-i18next";

/** Periods understood by the views endpoints (see afo-back services/periodViews.js). */
export const PERIODS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "Since the beginning" },
  { value: "custom", label: "Date range" },
];

const DAY = 24 * 60 * 60 * 1000;

/** YYYY-MM-DD (UTC) of a date, as expected by the API and date inputs. */
export const isoDay = (date) => new Date(date).toISOString().slice(0, 10);

/**
 * Period picked on a dashboard card. `filter` is what the views endpoints expect:
 * `{ period }`, or `{ period: "custom", start, end }`; `null` while the date range is invalid.
 */
export function usePeriodFilter(defaultPeriod = "30d") {
  const [period, setPeriod] = useState(defaultPeriod);
  const [range, setRange] = useState(() => ({
    start: isoDay(Date.now() - 29 * DAY),
    end: isoDay(Date.now()),
  }));
  const rangeValid = Boolean(range.start && range.end && range.start <= range.end);
  const filter =
    period === "custom" ? (rangeValid ? { period, ...range } : null) : { period };
  return { period, setPeriod, range, setRange, rangeValid, filter };
}

/** Period as a group of buttons. */
export const PeriodButtons = ({ period, setPeriod }) => {
  const { t } = useTranslation();
  return (
    <div className="dash-segmented" role="group" aria-label={t("Period")}>
      {PERIODS.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          aria-pressed={period === value}
          onClick={() => setPeriod(value)}
        >
          {t(label)}
        </button>
      ))}
    </div>
  );
};

/** Period as a select, for headers that already hold other controls. `label` names it. */
export const PeriodSelect = ({ period, setPeriod, label }) => {
  const { t } = useTranslation();
  return (
    <select
      className="dash-select"
      value={period}
      onChange={(e) => setPeriod(e.target.value)}
      aria-label={label || t("Period")}
    >
      {PERIODS.map(({ value, label }) => (
        <option value={value} key={value}>
          {t(label)}
        </option>
      ))}
    </select>
  );
};

/** Start and end dates, shown when the "Date range" period is picked. */
export const DateRangeFields = ({ period, range, setRange, rangeValid }) => {
  const { t } = useTranslation();
  if (period !== "custom") return null;
  const today = isoDay(Date.now());
  return (
    <div className="dash-date-range">
      <label>
        <span>{t("From")}</span>
        <input
          type="date"
          className="dash-select dash-date-input"
          value={range.start}
          max={range.end || today}
          onChange={(e) => setRange({ ...range, start: e.target.value })}
        />
      </label>
      <label>
        <span>{t("To")}</span>
        <input
          type="date"
          className="dash-select dash-date-input"
          value={range.end}
          min={range.start}
          max={today}
          onChange={(e) => setRange({ ...range, end: e.target.value })}
        />
      </label>
      {!rangeValid && (
        <p className="dash-range-error" role="alert">
          {t("The start date must be before the end date")}
        </p>
      )}
    </div>
  );
};

/** Note shown when views per period are not recorded back to the start of the period. */
export const IncompleteViewsNote = ({ data }) => {
  const { t, i18n } = useTranslation();
  if (!data || data.complete !== false) return null;
  return (
    <p className="dash-ranking-note">
      {data.trackedSince
        ? t(
            "Views per period are only recorded since {{date}}: earlier views are not counted.",
            {
              date: new Date(data.trackedSince).toLocaleDateString(i18n.language, {
                timeZone: "UTC",
              }),
            }
          )
        : t("Views per period are not recorded yet.")}
    </p>
  );
};
